/**
 * Builds the one stopped outcome every workflow run site reports.
 *
 * Consumers: `ArWorkflowMenu`, `ArTableControls`, `useFormWorkflowRun` and
 * `RelationshipTree`, each on the `success === false` branch of a resolved
 * `executeWorkflow` response. It sits beside `workflowRunErrorOutcome` and plays the
 * same role for the stopped arm, so all four sites word a stop identically (SYD-2443).
 *
 * `success === false` means stopped and nothing else: a failed run throws, so the
 * partial `actions` beside a `false` are the work that landed. The count of actions
 * the response carries is therefore how far the run got, and the run's own
 * `newFormId` still names a form that really was created.
 */

import type { ExecuteWorkflowResponse, Workflow } from '#types';
import type { WorkflowRunOutcome } from './workflowRunOutcome.types';

export const workflowRunStoppedOutcome = (
  result: ExecuteWorkflowResponse,
  workflow: Workflow,
): Extract<WorkflowRunOutcome, { status: 'stopped' }> => {
  const label = workflow.description?.shortLabel ?? 'The workflow';
  const completed = result.actions.length;
  const total = (workflow.actions ?? []).length;
  const createdForm = result.newFormId != null ? ' A new form was created.' : '';
  const summary = result.summaries?.find((s) => s.actionType === 'send');

  return {
    status: 'stopped',
    text: `${label} stopped after ${completed} of ${total} action${total === 1 ? '' : 's'}.${createdForm}`,
    ...(summary ? { summary } : {}),
  };
};
